import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {Alert} from '@mantine/core';
import {I18n} from '@aws-amplify/core';

import {
    FormPageLayout,
    PlatformLoader,
} from '@flexinet/ui-components';
import {Profile} from '../profile/Profile';
import {FormDrawer} from '../drawers/FormDrawer';

import {EditPageNavigation} from '../navigation/EditPageNavigation';
import {useBillingProvider} from '../hooks/useBillingProvider.ts';

import {useEditPageTools} from '../hooks/useEditPageTools';
import {BillingProvider} from '../types';
import {moduleConfig} from '../config';

export const EditPage = () => {

    const {id: billingProviderId} = useParams();

    if (typeof billingProviderId === 'undefined') {
        throw new Error('billingProviderId is required parameter');
    }

    const {
        record: billingProvider,
        loading,
        isError,
        updateRecord,
    } = useBillingProvider(billingProviderId);

    const [drawerOpened, setDrawerOpened] = useState(false);

    const {tools} = useEditPageTools({onEdit: () => setDrawerOpened(true)});

    if (loading) {
        return <PlatformLoader message={I18n.get('Loading billing provider...Please wait')}/>;
    }

    // A failed fetch, or an id that resolves to nothing (deleted, mistyped), used to spin
    // the loader forever.
    if (isError || !billingProvider) {
        return (
            <Alert color='red' title={I18n.get('Could not load this billing provider')} m='md'>
                {isError
                    ? I18n.get('The billing API did not return the billing provider. Try reloading the page.')
                    : `${I18n.get('No billing provider has the id')} ${billingProviderId}. ${I18n.get('It may have been deleted.')}`}
            </Alert>
        );
    }

    return (
        <FormPageLayout
            title={`${I18n.get(moduleConfig.titles.singular)}: ${billingProvider.name || billingProvider.key}`}
            pageNavigation={<EditPageNavigation component={billingProvider}/>}
            tools={tools}
        >
            <Profile record={billingProvider}/>

            {/* Resolves true only when the update landed; FormDrawer closes on that alone. */}
            <FormDrawer
                record={billingProvider}
                handleSubmit={(values: BillingProvider) => updateRecord(values)}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </FormPageLayout>
    );
};
